import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { X, Copy, RefreshCw, Check } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CONTEXT_LABEL = { nfce_emissao: 'Emissão NFC-e', nfce_retransmissao: 'Retransmissão (contingência)', nfce_cancelamento: 'Cancelamento NFC-e', nfce_xml: 'Download XML', empresa_sync: 'Sincronizar empresa' };
const pretty = (v) => (v === null || v === undefined ? '—' : typeof v === 'string' ? v : JSON.stringify(v, null, 2));

const CopyBtn = ({ text, id }) => {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(text); setDone(true); setTimeout(() => setDone(false), 1500); }
    catch (_e) { toast.error('Não foi possível copiar'); }
  };
  return (
    <button type="button" onClick={copy} className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded border border-slate-300 hover:bg-slate-100" data-testid={`focus-copy-${id}`}>
      {done ? <Check size={12} className="text-emerald-600" /> : <Copy size={12} />} {done ? 'Copiado' : 'Copiar'}
    </button>
  );
};

const Block = ({ title, value, id, mono = true }) => (
  <div data-testid={`focus-block-${id}`}>
    <div className="flex items-center justify-between mb-1">
      <h4 className="text-xs font-bold uppercase tracking-wide text-slate-600">{title}</h4>
      <CopyBtn text={pretty(value)} id={id} />
    </div>
    <pre className={`text-xs bg-slate-900 text-slate-100 rounded-md p-3 overflow-auto max-h-64 whitespace-pre-wrap break-all ${mono ? 'font-mono' : ''}`}>{pretty(value)}</pre>
  </div>
);

export const FocusLastRequestModal = ({ onClose }) => {
  const [rows, setRows] = useState(null);
  const [idx, setIdx] = useState(0);
  const load = () => axios.get(`${API}/nfce/focus-log?limit=10`, { withCredentials: true }).then((r) => { setRows(r.data); setIdx(0); }).catch((e) => { toast.error(e.response?.data?.detail || 'Falha ao carregar o log da Focus'); setRows([]); });
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const k = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [onClose]);

  const r = rows?.[idx];
  const fullText = r ? [
    `URL: ${r.method} ${r.url}`, `Ambiente: ${r.ambiente} · Referência: ${r.ref || '—'} · Data: ${new Date(r.at).toLocaleString('pt-BR')}`,
    `Token (5 primeiros): ${r.token_prefix || '—'}`, `Headers: ${pretty(r.headers)}`, `JSON enviado:\n${pretty(r.request_json)}`,
    `Resposta HTTP ${r.http_status ?? '(sem resposta)'}:\n${pretty(r.error || r.response)}`,
  ].join('\n\n') : '';

  return (
    <div className="fixed inset-0 z-[70] bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="focus-last-request-modal">
      <div className="bg-white rounded-xl w-full max-w-3xl max-h-[92vh] flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <div>
            <h3 className="text-lg font-bold text-slate-900">Última requisição à Focus NFe</h3>
            <p className="text-xs text-slate-500">Dados para enviar ao suporte da Focus. O token completo nunca é exibido.</p>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={load} data-testid="focus-log-refresh"><RefreshCw size={14} className="mr-1" /> Atualizar</Button>
            <button type="button" onClick={onClose} className="text-slate-500 hover:text-slate-900" data-testid="focus-log-close"><X size={20} /></button>
          </div>
        </div>

        <div className="overflow-y-auto p-5 space-y-4">
          {rows === null && <p className="text-sm text-slate-500">Carregando…</p>}
          {rows && rows.length === 0 && <p className="text-sm text-slate-600" data-testid="focus-log-empty">Nenhuma requisição à Focus NFe registrada ainda. Emita uma NFC-e (Cupom Fiscal) com o token configurado e volte aqui.</p>}
          {r && (
            <>
              {rows.length > 1 && (
                <select value={idx} onChange={(e) => setIdx(Number(e.target.value))} className="w-full h-9 px-2 rounded-md border border-slate-300 bg-white text-sm" data-testid="focus-log-select">
                  {rows.map((x, i) => <option key={i} value={i}>{new Date(x.at).toLocaleString('pt-BR')} · {CONTEXT_LABEL[x.context] || x.context || 'Focus'} · HTTP {x.http_status ?? 'erro de rede'} {x.ref ? `· ref ${x.ref}` : ''}</option>)}
                </select>
              )}
              <div className={`rounded-md border-2 p-3 text-sm ${r.ok ? 'bg-emerald-50 border-emerald-300' : 'bg-red-50 border-red-300'}`} data-testid="focus-log-status">
                <div className="font-bold">{r.ok ? 'Sucesso' : 'Falha'} — HTTP {r.http_status ?? '(sem resposta da Focus)'} · {CONTEXT_LABEL[r.context] || r.context}</div>
                <div className="text-xs text-slate-600 mt-1">Ambiente <b>{r.ambiente}</b> · Referência <b>{r.ref || '—'}</b> · {new Date(r.at).toLocaleString('pt-BR')}</div>
              </div>
              <Block title="1. URL completa da requisição" value={`${r.method} ${r.url}`} id="url" />
              <Block title="2. Token utilizado (5 primeiros caracteres)" value={r.token_prefix || '—'} id="token" />
              <Block title="3. Headers enviados" value={r.headers} id="headers" />
              <Block title="4. JSON de envio" value={r.request_json} id="json" />
              <Block title={`5. Resposta da Focus (HTTP ${r.http_status ?? 'sem resposta'})`} value={r.error || r.response} id="response" />
            </>
          )}
        </div>

        {r && (
          <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-200">
            <CopyBtn text={fullText} id="all" />
            <Button variant="outline" onClick={onClose}>Fechar</Button>
          </div>
        )}
      </div>
    </div>
  );
};
